import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Inject,
  InjectionToken,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { fromEvent, Observable } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DataService } from '../services/data.service';

/**
 * Keyboard events
 */
const isArrowUp = (keyCode) => keyCode === 38;
const isArrowDown = (keyCode) => keyCode === 40;
const isArrowUpDown = (keyCode) => isArrowUp(keyCode) || isArrowDown(keyCode);
const isEnter = (keyCode) => keyCode === 13;
const isBackspace = (keyCode) => keyCode === 8;
const isDelete = (keyCode) => keyCode === 46;
const isESC = (keyCode) => keyCode === 27;
const isTab = (keyCode) => keyCode === 9;

let batchAddress;

const allowedDisplayData = ['state', 'address', 'name', 'zip', 'city'];

const capitalizeWords = (str: string) => {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const styleDisplayData = (type: string, value: string) => {
  switch (type) {
    case 'city':
    case 'address':
      return capitalizeWords(value);
    case 'state':
      return value.toUpperCase();
    default:
      return value;
  }
};

@Component({
  selector: 'bs-address-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  host: {
    '(document:click)': 'handleClick($event)',
    class: 'ng-autocomplete',
  },
})
export class AutocompleteComponent
  implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor
{
  @ViewChild('searchInput') searchInput: ElementRef; // input element
  @ViewChild('filteredListElement') filteredListElement: ElementRef; // element of items

  inputKeyUp$: Observable<any>;
  inputKeyDown$: Observable<any>;

  public query = ''; // search query
  public filteredList = []; // list of items
  public elementRef;
  public selectedIdx = -1;
  public toHighlight = '';
  public notFound = false;
  public isFocused = false;
  public isOpen = false;
  public overlay = false;
  private manualOpen = undefined;
  private manualClose = undefined;
  public batchTokenData;
  public isLoading = false;
  public isRecentSearchOpen;

  // @Inputs
  /**
   * Data of items list.
   * It can be array of strings or array of objects.
   */
  @Input() public data = [];
  @Input() public searchKeyword: string; // keyword to filter the list
  @Input() public placeholder = 'Enter your address';
  @Input() public heading = '';
  @Input() public initialValue: any;
  @Input() public showNotFound = true;
  @Input() public take: number;
  @Input() public displayData: string;
  @Input() public clearInputOnSelect = false;

  // @Input() public isLoading: boolean; // loading mask
  @Input() public notFoundText = 'No results found'; // set custom text when filter returns empty result
  @Input() public recentSearch: Record<string, string>[];
  @Input() public debounceTime = 700; // delay time while typing
  @Input() public disabled: boolean; // input disable/enable
  @Input() public filterType = [];
  
  /**
   * The minimum number of characters the user must type before a search is performed.
   */
  @Input() public minQueryLength = 1;

  /**
   * Focus first item in the list
   */
  @Input() public focusFirst = false;

  // @Output events
  /** Event that is emitted whenever an item from the list is selected. */
  @Output() selected = new EventEmitter<any>();

  /** Event that is emitted whenever an input is changed. */
  @Output() inputChanged = new EventEmitter<any>();

  /** Event that is emitted whenever an input is focused. */
  @Output() readonly inputFocused: EventEmitter<void> =
    new EventEmitter<void>();

  /** Event that is emitted whenever an input is cleared. */
  @Output() readonly inputCleared: EventEmitter<void> =
    new EventEmitter<void>();

  /** Event that is emitted when the autocomplete panel is opened. */
  @Output() readonly opened: EventEmitter<void> = new EventEmitter<void>();

  /** Event that is emitted when the autocomplete panel is closed. */
  @Output() readonly closed: EventEmitter<void> = new EventEmitter<void>();

  @Output() handleError = new EventEmitter<any>();
  @Output() suggestionList = new EventEmitter<any>();

  // Custom templates
  @Input() itemTemplate!: TemplateRef<any>;
  @Input() notFoundTemplate!: TemplateRef<any>;

  /**
   * Propagates new value when model changes
   */
  propagateChange: any = () => {};

  onTouched: any = () => {};

  @HostListener('window:click', ['$event', '$event.target'])
public onClick(event: MouseEvent, targetElement: HTMLElement): void {
    if (!targetElement) {
        return;
    }
    const clickedInside = this.elementRef.nativeElement.contains(targetElement);
    if (!clickedInside) {
    this.isRecentSearchOpen = false;
    }
}

  /**
   * Writes a new value from the form model into the view,
   * Updates model
   */
  writeValue(value: any = '') {
    this.query = value[this.searchKeyword];
  }

  /**
   * Registers a handler that is called when something in the view has changed
   */
  registerOnChange(fn) {
    this.propagateChange = fn;
  }

  /**
   * Registers a handler specifically for when a control receives a touch event
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Event that is called when the value of an input element is changed
   */
  onChange(event) {
    // close recent search dropdown user input value.
    this.isRecentSearchOpen = event.target.value ? false : true;

    this.propagateChange(event.target.value);
    if (!event.target.value) { this.filteredList = []; }
  }

  constructor(
    elementRef: ElementRef,
    private renderer: Renderer2,
    public dataService: DataService,
    @Inject('Params') batchTokenData: string[]
  ) {
    this.elementRef = elementRef;
    this.batchTokenData = batchTokenData; // Here in token data we will get API_KEY and DOMAIN_SERVER from client
  }

  /**
   * Event that is called when the control status changes to or from DISABLED
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit(): void {
    this.isRecentSearchOpen = false;
  }

  ngAfterViewInit() {
    this.initEventStream();
  }

  /**
   * Set initial value
   * @param value intitial value
   */
  public setInitialValue(value: any) {
    if (this.initialValue) {
      this.select(value);
    }
  }

  /**
   * Update search results
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.setInitialValue(this.initialValue);
    if (changes && changes.data && Array.isArray(changes.data.currentValue)) {
      this.handleItemsChange();
      if (!changes.data.firstChange && this.isFocused) {
        this.handleOpen();
      }
    }
  }

  /**
   * Items change
   */
  public handleItemsChange() {
    if (!this.isOpen) {
      return;
    }
    this.filteredList = this.data;
    this.notFound = !this.filteredList || this.filteredList.length === 0;

    // Filter list when updating data and panel is open
    if (this.isOpen) {
      this.filterList();
    }
  }

  /**
   * Filter data
   */
  public async filterList() {
    this.selectedIdx = -1;
    this.isLoading = true;
    try {
      if (this.query.trim().length >= 3) {
        const data = await this.dataService.getAddresses(
          this.query,
          this.batchTokenData,
          this.take,
          this.filterType
        );
       let apiResponse = data[`result`]?.suggestions;
        batchAddress = apiResponse;
       
        this.isRecentSearchOpen = false;
        this.suggestionList.emit(batchAddress);
        if (batchAddress.length === 0 && this.showNotFound) {
          this.notFound = true;
        }
      }

      if (this.query != null && batchAddress) {
        this.toHighlight = this.query;
        this.filteredList = batchAddress;
        if (this.filteredList.length > 0 && this.focusFirst) {
          this.selectedIdx = 0;
        }
        this.isLoading = false;
      } else {
        this.isLoading = false;
        this.notFound = false;
      }
    } catch (error) {
      this.isLoading = false;
      this.handleError.emit(error);
    }
  }

  /**
   * Select item in the list.
   * @param item item detail
   */
  public select(item) {
    if (
      Boolean(this.displayData) &&
      allowedDisplayData.includes(this.displayData.toLowerCase()) &&
      item[this.displayData]
    ) {
      this.query = styleDisplayData(this.displayData, item[this.displayData]);
    } else {
      this.query = item.name;
    }

    this.isOpen = true;
    this.overlay = false;
    if (this.filteredList.length > 0) {
      this.selected.emit(item);
    }
    this.propagateChange(item);
    this.handleClose();
  }

  public recentSearchClick(item) {
    this.select(item);
    this.isRecentSearchOpen = false;
    this.filterList();
  }

  /**
   * Document click
   * @param e event
   */
  public handleClick(e) {
    let clickedComponent = e.target;
    let inside = false;
    do {
      if (clickedComponent === this.elementRef.nativeElement) {
        inside = true;
        if (this.filteredList.length) {
          this.handleOpen();
        }
      }
      clickedComponent = clickedComponent.parentNode;
    } while (clickedComponent);
    if (!inside) {
      this.handleClose();
    }
  }

  /**
   * Handle body overlay
   */
  handleOverlay() {
    this.overlay = false;
  }

  /**
   * Define panel state
   */
  setPanelState(event) {
    if (event) {
      event.stopPropagation();
    }
    // If controls are untouched
    if (
      typeof this.manualOpen === 'undefined' &&
      typeof this.manualClose === 'undefined'
    ) {
      this.isOpen = false;
      this.handleOpen();
    }

    // If one of the controls is untouched and other is deactivated
    if (
      (typeof this.manualOpen === 'undefined' && this.manualClose === false) ||
      (typeof this.manualClose === 'undefined' && this.manualOpen === false)
    ) {
      this.isOpen = false;
      this.handleOpen();
    }

    // if controls are touched but both are deactivated
    if (this.manualOpen === false && this.manualClose === false) {
      this.isOpen = false;
      this.handleOpen();
    }

    // if open control is touched and activated
    if (this.manualOpen) {
      this.isOpen = false;
      this.handleOpen();
      this.manualOpen = false;
    }

    // if close control is touched and activated
    if (this.manualClose) {
      this.isOpen = true;
      this.handleClose();
      this.manualClose = false;
    }
  }

  /**
   * Manual controls
   */
  open() {
    this.manualOpen = true;
    this.isOpen = false;
    this.handleOpen();
  }

  close() {
    this.manualClose = true;
    this.isOpen = true;
    this.handleClose();
  }

  focus() {
    this.handleFocus(event); // tslint:disable-line
  }

  clear() {
    this.remove(event); // tslint:disable-line
  }

  clearInputOnDemand() {
    this.query = '';
    this.inputCleared.emit();
  }
  
  /**
   * Remove search query
   */
  public remove(e) {
    e.stopPropagation();
    this.query = '';
    this.inputCleared.emit();
    this.selected.emit(null);
    this.propagateChange(this.query);
    this.setPanelState(e);
    this.isRecentSearchOpen = true;
    this.filteredList = [];
    if (this.data && !this.data.length) {
      this.notFound = false;
    }
  }

  handleOpen() {
    if (this.isOpen || (this.isOpen && !this.isLoading)) {
      return;
    }
    // If data exists
    if (this.data && this.data.length) {
      this.isOpen = true;
      this.overlay = true;
      this.filterList();
      this.opened.emit();
    }

  }

  handleClose() {
    if (!this.isOpen) {
      this.isFocused = false;
      if (this.filteredList.length) {
        this.filteredList = [];
        this.selectedIdx = -1;
      }
      return;
    }
    this.isOpen = false;
    this.overlay = false;
    this.filteredList = [];
    this.selectedIdx = -1;
    this.notFound = false;
    this.isFocused = false;
    this.closed.emit();
    if(this.clearInputOnSelect && !this.isRecentSearchOpen) {
      this.clearInputOnDemand()
    }
  }

  handleFocus(e) {
    this.searchInput.nativeElement.focus();
    // if data is not available in input filed
    if (!this.query.length) {
      this.isRecentSearchOpen = true;
    }
    if (this.isFocused) {
      return;
    }
    this.inputFocused.emit(e);
    // if data exists then open
    if (this.data && this.data.length) {
      this.setPanelState(event); // tslint:disable-line
    }
    this.isFocused = true;

  }

  /**
   * Initialize keyboard events
   */
  initEventStream() {
    this.inputKeyUp$ = fromEvent(this.searchInput.nativeElement, 'keyup').pipe(
      map((e: any) => e)
    );

    this.inputKeyDown$ = fromEvent(
      this.searchInput.nativeElement,
      'keydown'
    ).pipe(map((e: any) => e));

    this.listenEventStream();
  }

  /**
   * Listen keyboard events
   */
  listenEventStream() {
    // key up event
    this.inputKeyUp$
      .pipe(
        filter(
          (e) =>
            !isArrowUpDown(e.keyCode) &&
            !isEnter(e.keyCode) &&
            !isESC(e.keyCode) &&
            !isTab(e.keyCode)
        ),
        debounceTime(this.debounceTime)
      )
      .subscribe((e) => {
        this.onKeyUp(e);
      });

    // cursor up & down
    this.inputKeyDown$
      .pipe(filter((e) => isArrowUpDown(e.keyCode)))
      .subscribe((e) => {
        e.preventDefault();
        this.onFocusItem(e);
      });

    // enter keyup
    this.inputKeyUp$.pipe(filter((e) => isEnter(e.keyCode))).subscribe((e) => {
      // this.onHandleEnter();
    });

    // enter keydown
    this.inputKeyDown$
      .pipe(filter((e) => isEnter(e.keyCode)))
      .subscribe((e) => {
        this.onHandleEnter();
      });

    // ESC
    this.inputKeyUp$
      .pipe(filter((e) => isESC(e.keyCode), debounceTime(100)))
      .subscribe((e) => {
        this.onEsc();
      });

    // TAB
    this.inputKeyDown$.pipe(filter((e) => isTab(e.keyCode))).subscribe((e) => {
      this.onTab();
    });
  }

  /**
   * on keyup == when input changed
   * @param e event
   */
  onKeyUp(e) {
    this.notFound = false; // search results are unknown while typing
    // if input is empty
    if (!this.query) {
      this.notFound = false;
      this.inputChanged.emit(e.target.value);
      this.inputCleared.emit();
      this.setPanelState(e);
      this.selected.emit(null);
    }
    // note that '' can be a valid query
    if (!this.query && this.query !== '') {
      return;
    }
    // if query >= to minQueryLength
    if (this.query.length >= this.minQueryLength) {
      this.inputChanged.emit(e.target.value);
      this.filterList();

      if (this.data && !this.data.length) {
        this.isOpen = true;
      }
    }
  }

  /**
   * Keyboard arrow top and arrow bottom
   * @param e event
   */
  onFocusItem(e) {
    // move arrow up and down on filteredList
    if (this.filteredList.length) {
      // filteredList
      const totalNumItem = this.filteredList.length;
      if (e.key === 'ArrowDown') {
        let sum = this.selectedIdx;
        sum = this.selectedIdx === null ? 0 : sum + 1;
        this.selectedIdx = (totalNumItem + sum) % totalNumItem;
      } else if (e.key === 'ArrowUp') {
        if (this.selectedIdx === -1) {
          this.selectedIdx = 0;
        }
        this.selectedIdx = (totalNumItem + this.selectedIdx - 1) % totalNumItem;
      }
    }
  }

  /**
   * Select item on enter click
   */
  onHandleEnter() {
    // click enter to choose item from filteredList
    if (this.selectedIdx > -1) {
      // filteredList
      this.query = this.filteredList[this.selectedIdx]
        ? this.filteredList[this.selectedIdx][this.searchKeyword]
        : this.filteredList[this.selectedIdx];

      this.select(this.filteredList[this.selectedIdx]);
    }
    this.handleClose();
  }

  /**
   * Esc click
   */
  onEsc() {
    this.searchInput.nativeElement.blur();
    this.handleClose();
  }

  /**
   * Tab click
   */
  onTab() {
    this.searchInput.nativeElement.blur();
    this.handleClose();
  }
}
